import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOpenPlans } from '@/hooks/use-open-plans';
import DownloadUpsellDialog from '@/components/DownloadUpsellDialog';
import { DownloadUpsellContext } from '@/contexts/downloadUpsellContext';
import { useMyDataQuery } from '@/generated/graphql';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { isPaidSubscription } from '@/lib/quota/isPaidSubscription';
import {
  getDownloadUpsellStorage,
  recordDownload,
  shouldShowAfterDownload,
} from '@/lib/upsell/downloadUpsellCadence';

/** Let the success toast land and the file finish saving before interrupting. */
const OPEN_DELAY_MS = 800;

interface Props {
  children: React.ReactNode;
}

/**
 * Owns the post-download upgrade nudge for the whole app. Mounted once by
 * `Layout` so the seven `downloadClipVideo` call sites share one counter and
 * one dialog instead of each growing their own copy of the rule.
 */
export const DownloadUpsellProvider = ({ children }: Props) => {
  const openPlans = useOpenPlans();
  const [open, setOpen] = useState(false);
  const { data } = useMyDataQuery();
  const countRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Read through a ref so `notifyClipDownloaded` keeps a stable identity —
  // it is passed down into memoized clip cards.
  const isPaidRef = useRef(false);
  isPaidRef.current = isPaidSubscription(data?.myData?.subscription);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const notifyClipDownloaded = useCallback(() => {
    // Paid users are not counted at all: burning milestones invisibly would
    // leave a churned user with no nudge left when they land back on free.
    if (isPaidRef.current) return;

    const storage = getDownloadUpsellStorage(
      typeof window === 'undefined' ? {} : window,
    );
    const count = recordDownload(storage);
    if (!shouldShowAfterDownload(count)) return;

    countRef.current = count;
    trackEvent('download_upsell_shown', { download_count: count });

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  }, []);

  const handleUpgrade = useCallback(() => {
    trackEvent('download_upsell_upgrade_clicked', {
      download_count: countRef.current,
    });
    setOpen(false);
    openPlans();
  }, [openPlans]);

  const contextValue = useMemo(
    () => ({ notifyClipDownloaded }),
    [notifyClipDownloaded],
  );

  return (
    <DownloadUpsellContext.Provider value={contextValue}>
      {children}
      {/* Mounted only while open: `ProFeatureDialog`'s price anchor queries
          the plans list, which must not run on every authenticated page. */}
      {open && (
        <DownloadUpsellDialog
          open
          onOpenChange={setOpen}
          onUpgrade={handleUpgrade}
        />
      )}
    </DownloadUpsellContext.Provider>
  );
};

export default DownloadUpsellProvider;
